import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useCreateMutation } from "../../../api/genericHooks";
import { BookContext } from "../../../contexts/BookContext";
import { Image } from "../../../types/models/Image";
import { theme } from "../../../utils/theme";
import { ImageUploader } from "../../ImageUploader";
import { Step } from "../GenerateBookPDFModal";
import { GenPDFStepper } from "./GenPDFStepper";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  steps: Step[];
  nextStep: () => void;
};

export type GenerateBookPDFData = {
  cover_text_color: string;
  cover_bg_color: string;
  cover_image: string | File;
};

export const GenerateBookPDFModalStep2: FC<Props> = ({
  open,
  setOpen,
  steps,
  nextStep,
}) => {
  const { t } = useTranslation();
  const { book, refresh } = useContext(BookContext);
  const [coverImage, setCoverImage] = useState<Image | undefined>(
    book?.cover_image_url
      ? {
          url: book?.cover_image_url,
        }
      : undefined
  );

  // Form Methods =============================================================
  const GeneratePDF1FormSchema = Yup.object().shape({
    cover_text_color: Yup.string().required(t("errors.required")),
    cover_bg_color: Yup.string().required(t("errors.required")),
    cover_image: Yup.mixed<File | string>()
      .required(t("errors.required"))
      .test(
        "fileType",
        t("errors.invalidFileType"),
        (value) => typeof value === "string" || value instanceof File
      ),
  });

  const defaultValues = useMemo<GenerateBookPDFData>(() => {
    return {
      cover_text_color: book?.cover_text_color || theme.colors.rust[500],
      cover_bg_color: book?.cover_bg_color || "#ffffff",
      cover_image: (book?.cover_image_url || "") as string | File,
    };
  }, [book]);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<GenerateBookPDFData>({
    defaultValues: defaultValues,
    resolver: yupResolver(GeneratePDF1FormSchema),
  });

  const { cover_bg_color, cover_text_color } = watch();

  // Mutations ===============================================================
  const generateBookPDFMutation = useCreateMutation(
    "/update_cover_image",
    {},
    false,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  useEffect(() => {
    setValue("cover_image", coverImage?.file || "");
  }, [coverImage]);

  // Form Submit =============================================================
  const onSubmit = useCallback(
    (values: GenerateBookPDFData) => {
      const formData = new FormData();
      formData.append("book[cover_text_color]", values.cover_text_color);
      formData.append("book[cover_bg_color]", values.cover_bg_color);
      formData.append("book[cover_image]", values.cover_image as File);

      generateBookPDFMutation.mutateAsync(formData).then(() => {
        toast.success(t("generateBookPDFModal.saveSuccess"));
        refresh();
        nextStep();
      });
    },
    [refresh, t]
  );

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-3 sm:px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 w-full mx-2 sm:mx-0"
          >
            <form
              className="bg-white flex flex-1 flex-col justify-between"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="absolute right-0 top-0 pr-3 pt-3 sm:pr-4 sm:pt-4 block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon
                    aria-hidden="true"
                    className="w-5 h-5 sm:w-6 sm:h-6"
                  />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <DialogTitle
                    as="h3"
                    className="text-xl sm:text-2xl font-semibold text-slate-800 pr-8 sm:pr-0"
                  >
                    {t("generateBookPDFModal.title")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />

                  <GenPDFStepper steps={steps} />

                  <div className="flex flex-row mt-5">
                    <div className="flex flex-col w-2/6 border-r-2 border-rust-800">
                      <p className="font-semibold text-center text-sm text-slate-700 mb-2">
                        {t("generateBookPDFModal.coverImage")}
                      </p>
                      <ImageUploader
                        image={coverImage}
                        image_url={book?.cover_image_url}
                        setImage={setCoverImage}
                        label={t("generateBookPDFModal.addCover")}
                      />

                      {errors.cover_image && (
                        <span className="text-rust-400 font-nunito">
                          {errors.cover_image.message}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col w-4/6">
                      <div className="flex flex-row">
                        <div className="w-1/2">
                          <div className="mx-4">
                            <div>
                              <p className="font-semibold text-sm text-slate-700 mb-2">
                                {t("generateBookPDFModal.coverTextColor")}
                              </p>
                              <div className="w-2/3 mt-2 flex">
                                <div
                                  style={{ backgroundColor: cover_text_color }}
                                  className="flex shrink-0 items-center rounded-l-md px-4 text-base text-gray-500 outline outline-1 -outline-offset-1 outline-gray-300 sm:text-sm/6"
                                ></div>
                                <div className="relative -ml-px block w-full grow">
                                  <input
                                    id="cover_text_color"
                                    name="cover_text_color"
                                    type="color"
                                    defaultValue="#ffffff"
                                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                                    onChange={(e) => {
                                      setValue(
                                        "cover_text_color",
                                        e.target.value
                                      );
                                    }}
                                  />
                                  <div
                                    id="color-display"
                                    className="flex items-center rounded-r-md px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 h-full"
                                  >
                                    <span id="color-text" className="text-sm/6">
                                      {cover_text_color}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="w-1/2">
                          <div className="mx-4">
                            <div>
                              <p className="font-semibold text-sm text-slate-700 mb-2">
                                {t("generateBookPDFModal.coverBgColor")}
                              </p>
                              <div className="w-2/3 mt-2 flex">
                                <div
                                  style={{ backgroundColor: cover_bg_color }}
                                  className="flex shrink-0 items-center rounded-l-md px-4 text-base text-gray-500 outline outline-1 -outline-offset-1 outline-gray-300 sm:text-sm/6"
                                ></div>
                                <div className="relative -ml-px block w-full grow">
                                  <input
                                    id="cover_text_color"
                                    name="cover_text_color"
                                    type="color"
                                    defaultValue="#ffffff"
                                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                                    onChange={(e) => {
                                      setValue(
                                        "cover_bg_color",
                                        e.target.value
                                      );
                                    }}
                                  />
                                  <div
                                    id="color-display"
                                    className="flex items-center rounded-r-md px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 h-full"
                                  >
                                    <span id="color-text" className="text-sm/6">
                                      {cover_bg_color}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="w-full mt-4 mx-4 items-center">
                        <p className="font-semibold">
                          {t("generateBookPDFModal.preview")}
                        </p>
                        <div
                          style={{
                            backgroundColor: cover_bg_color,
                            color: cover_text_color,
                          }}
                          className="w-2/3 h-24 border mt-2 border-gray-200 rounded-lg flex items-center justify-center"
                        >
                          <span className="text-center text-lg font-semibold">
                            {t("generateBookPDFModal.previewTitle")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col sm:flex-row-reverse gap-3 sm:gap-0">
                <button
                  type="submit"
                  className="inline-flex w-full justify-center rounded bg-rust-50 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:w-auto"
                >
                  {t("addPicturesModal.save")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
